import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { LoadingState, ErrorState, EmptyState } from "@/components/States";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, ArrowLeft, RotateCcw, Clock, Globe, Tag, AlertTriangle } from "lucide-react";

function formatRemaining(seconds) {
  if (seconds <= 0) return "Vaxt bitdi";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (d > 0) return `${d} gün ${h} saat qalıb`;
  if (h > 0) return `${h} saat ${m} dəqiqə qalıb`;
  if (m > 0) return `${m} dəqiqə ${s} saniyə qalıb`;
  return `${s} saniyə qalıb`;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("az-AZ", {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function TrashRow({ item, onRestore }) {
  const [confirming, setConfirming] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [remaining, setRemaining] = useState(item.remaining_seconds);

  useEffect(() => {
    if (remaining <= 0) return;
    const t = setInterval(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
    return () => clearInterval(t);
  }, [remaining]);

  const doRestore = async () => {
    setRestoring(true);
    try {
      await api.post(`/channels/${item.id}/undo-delete`);
      toast.success(`"${item.name}" bərpa edildi`);
      setConfirming(false);
      onRestore();
    } catch (e) {
      const msg = apiError(e);
      toast.error(msg);
    } finally {
      setRestoring(false);
    }
  };

  const expired = remaining <= 0;

  return (
    <>
      <div
        className={`bg-[#111827] border rounded-xl p-4 flex flex-wrap items-center gap-4 transition-colors ${
          expired ? "border-slate-800/50 opacity-60" : "border-slate-800/80"
        }`}
        data-testid={`trash-item-${item.id}`}
      >
        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-none bg-rose-500/10 border border-rose-500/30">
          <Trash2 className="w-4 h-4 text-rose-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-semibold text-slate-100 truncate" data-testid={`trash-name-${item.id}`}>
            {item.name}
          </h3>
          <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500 mt-0.5">
            {item.niche && (
              <span className="flex items-center gap-1"><Tag className="w-3 h-3" /> {item.niche}</span>
            )}
            {item.language && (
              <span className="flex items-center gap-1"><Globe className="w-3 h-3" /> {item.language}</span>
            )}
            <span>Silinib: {formatDate(item.deleted_at)}</span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span
            className={`text-xs px-2.5 py-1 rounded-full flex items-center gap-1.5 ${
              expired
                ? "bg-slate-800 text-slate-500 border border-slate-700"
                : remaining < 30
                  ? "bg-rose-500/10 text-rose-300 border border-rose-500/30"
                  : "bg-amber-500/10 text-amber-300 border border-amber-500/30"
            }`}
            data-testid={`trash-countdown-${item.id}`}
          >
            <Clock className="w-3 h-3" />
            {expired ? "Vaxt bitdi" : formatRemaining(remaining)}
          </span>
          <Button
            size="sm"
            onClick={() => setConfirming(true)}
            disabled={expired || restoring}
            className="bg-emerald-600 hover:bg-emerald-500 text-white"
            data-testid={`restore-btn-${item.id}`}
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
            Bərpa et
          </Button>
        </div>
      </div>

      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent className="bg-[#111827] border-slate-700 text-slate-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Kanalı bərpa et?</DialogTitle>
            <DialogDescription className="text-slate-400">
              <strong className="text-slate-200">{item.name}</strong> kanalı bərpa ediləcək
              və əsas siyahıya qayıdacaq. Bütün məlumatlar olduğu kimi qalacaq.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirming(false)}
              className="border-slate-700 text-slate-300" data-testid={`restore-cancel-${item.id}`}>
              Ləğv et
            </Button>
            <Button onClick={doRestore} disabled={restoring}
              className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid={`restore-confirm-${item.id}`}>
              {restoring ? "Bərpa edilir…" : "Bərpa et"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function TrashBin() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await api.get("/channels/trash");
      setItems(data.items);
    } catch (e) {
      setError(apiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 10s to keep countdowns accurate
  useEffect(() => {
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  if (error) return <ErrorState message={error} onRetry={load} />;

  return (
    <div className="animate-in-up" data-testid="trash-bin-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold tracking-widest uppercase text-rose-400 mb-1 flex items-center gap-1.5">
            <Trash2 className="w-3.5 h-3.5" /> Zibil qutusu
          </p>
          <h1 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight text-slate-50">
            Son silinənlər
          </h1>
          <p className="text-sm text-slate-400 mt-2">
            Silinmiş kanallar burada müvəqqəti saxlanılır. Bərpa pəncərəsi bitdikdən sonra
            kanal həmişəlik silinir.
          </p>
        </div>
        <Button
          variant="outline" onClick={() => navigate("/channels")}
          className="border-slate-700 text-slate-300 hover:bg-slate-800"
          data-testid="back-to-channels"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Kanallara qayıt
        </Button>
      </div>

      {!items ? (
        <LoadingState label="Zibil qutusu yüklənir…" />
      ) : items.length === 0 ? (
        <EmptyState
          icon={Trash2}
          title="Zibil qutusu boşdur"
          description="Silinmiş kanal yoxdur. Bərpa pəncərəsi keçmiş kanallar avtomatik həmişəlik silinir."
          data-testid="trash-empty"
        />
      ) : (
        <div className="space-y-3" data-testid="trash-list">
          <p className="text-xs text-slate-500 flex items-center gap-1.5 mb-1">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
            {items.length} kanal bərpa pəncərəsində. Vaxt bitdikdən sonra həmişəlik silinəcək.
          </p>
          {items.map((item) => (
            <TrashRow key={item.id} item={item} onRestore={load} />
          ))}
        </div>
      )}
    </div>
  );
}
